import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import { api } from '../../api.ts';
import { settingsSchema } from '../../../shared/schemas.ts';
import { ErrorBox, Loading, PageHeader, Spinner, useDocumentTitle, useToast } from '../../components/ui.tsx';

type Values = z.input<typeof settingsSchema>;

export function SettingsPage() {
  useDocumentTitle('Asetukset');
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Array<{ name: string; value: string }>>('/admin/settings'),
  });
  const { register, control, handleSubmit, reset, formState } = useForm<Values>({ resolver: zodResolver(settingsSchema), defaultValues: { settings: [] } });
  const { fields, append, remove } = useFieldArray({ control, name: 'settings' });
  useEffect(() => {
    if (q.data) reset({ settings: q.data.map((s) => ({ name: s.name, value: s.value })) });
  }, [q.data, reset]);
  const save = useMutation({
    mutationFn: (d: z.output<typeof settingsSchema>) => api.put('/admin/settings', d),
    onSuccess: () => {
      toast.ok('Asetukset tallennettu');
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void qc.invalidateQueries({ queryKey: ['report'] });
    },
    onError: toast.error,
  });

  return (
    <div className="page page-narrow stack">
      <PageHeader title="Asetukset ja varmuuskopio" />
      <form className="card form" onSubmit={handleSubmit((d) => save.mutate(d))} noValidate>
        <div className="card-title">
          <h2>Muut asetukset</h2>
        </div>
        <p className="muted small" style={{ margin: 0 }}>
          <code>vertaile_raportti_&lt;id&gt;</code> = päivämäärä (VVVV-KK-PP): taulukossa merkitään tähdellä käyttäjät, jotka eivät ole
          päivittäneet kiinteitä arvojaan päivän jälkeen. Tyhjä arvo poistaa asetuksen.
        </p>
        {q.isLoading ? (
          <Loading />
        ) : (
          <div className="stack">
            {fields.map((f, i) => (
              <div key={f.id} className="row">
                <input className="input mono" style={{ flex: 2 }} placeholder="nimi" {...register(`settings.${i}.name`)} />
                <input className="input mono" style={{ flex: 1 }} placeholder="arvo" {...register(`settings.${i}.value`)} />
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => remove(i)} aria-label="Poista">
                  ×
                </button>
              </div>
            ))}
            <div>
              <button type="button" className="btn btn-sm" onClick={() => append({ name: '', value: '' })}>
                + Lisää asetus
              </button>
            </div>
          </div>
        )}
        {formState.errors.settings && <div className="alert alert-error">Tarkista asetusten nimet.</div>}
        <ErrorBox error={q.error} />
        <div className="form-actions">
          <button className="btn btn-primary" type="submit" disabled={save.isPending}>
            {save.isPending ? <Spinner /> : 'Tallenna'}
          </button>
        </div>
      </form>

      <div className="card">
        <div className="card-title">
          <h2>Varmuuskopio</h2>
        </div>
        <p className="muted">
          Lataa koko tietokanta (SQLite) talteen. Palvelimella ajetaan lisäksi automaattinen yöllinen varmuuskopio (ks. README).
        </p>
        <a className="btn" href="/api/admin/backup" download>
          Lataa tietokanta
        </a>
      </div>
    </div>
  );
}
